/* GENERATED from src/js/ndog-sticky-atc.js — edit the source, then run `make js`. Never edit this file by hand. */
/*
  NDOG Sticky Add-to-Cart (Mobile)
  --------------------------------
  Pins the EXISTING add-to-cart button to the bottom of the screen on mobile.
  No duplicate button: the real button stays a child of the product form, so all
  submit / loading / availability / app behaviour is untouched.

  On mobile the inline button is hidden entirely (CSS) — the button only ever shows
  as the sticky bar. The bar slides in once the variant block ([data-sticky-trigger])
  has scrolled up far enough that its BOTTOM edge reaches the bottom of the viewport,
  and slides out again (with hysteresis) when it drops back below on scroll-up. When
  no such block exists (e.g. single-variant products) it falls back to the sentinel,
  a zero-height marker reporting the original button position. A stick/release
  hysteresis prevents flicker at the threshold.

  Optional, independently toggled info (mirrored live from the page):
    - selected set size / configuration  (data-show-config)
    - current price                      (data-show-price)
*/
if (!customElements.get('ndog-sticky-atc')) {
  customElements.define(
    'ndog-sticky-atc',
    class NdogStickyAtc extends HTMLElement {
      // Fallback trigger (no variant block): the bar slides in once the zero-height
      // sentinel marker sits this many px above the viewport bottom. Larger = later.
      static STICK_OFFSET = 56;

      // Primary trigger: px above the viewport bottom at which the trigger element's
      // BOTTOM edge sticks the bar. 0 = exactly when its bottom meets the screen bottom.
      // Positive = appears later (scroll further); negative = appears earlier.
      static TRIGGER_OFFSET = 0;

      // Separate release line (px) so stick/unstick don't toggle at the same point —
      // required to stop flicker, since the marker position no longer depends on state.
      static STICK_HYSTERESIS = 48;

      // `bottom` (px) the WhatsApp widget is lifted to while the bar is shown.
      static WIDGET_BOTTOM = 62;

      connectedCallback() {
        this.sentinel = this.querySelector('[data-sticky-sentinel]');
        this.bar = this.querySelector('[data-sticky-bar]');
        this.info = this.querySelector('[data-sticky-info]');
        this.configEl = this.querySelector('[data-sticky-config]');
        this.priceEl = this.querySelector('[data-sticky-price]');
        this.form = this.closest('form');

        // Primary trigger element: the variant block flagged in Liquid (only rendered
        // when the sticky feature is on and the product has variants). When absent we
        // fall back to the sentinel below.
        this.triggerEl = this.form && this.form.querySelector('[data-sticky-trigger]');

        // Markup is incomplete or feature mis-rendered — fail safe, do nothing.
        if (!this.sentinel || !this.bar) return;

        this._stuck = false;
        this._stickRaf = 0;
        this._updateRaf = 0;
        this._leaveTimer = 0;
        this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        // Bind handlers once so we can remove them on disconnect.
        this._scheduleSticky = this._scheduleSticky.bind(this);
        this._scheduleUpdate = this._scheduleUpdate.bind(this);
        this._applyMode = this._applyMode.bind(this);

        this.mql = window.matchMedia('(max-width: 749px)');

        // Watch the live price element for programmatic text changes (variant/qty JS).
        const priceSource = this.form && this.form.querySelector('#price');
        if (priceSource && this.priceEl) {
          this.priceObserver = new MutationObserver(this._scheduleUpdate);
          this.priceObserver.observe(priceSource, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }

        // Re-read mirrored info on any variant / set size / quantity / plan change.
        if (this.form && (this.configEl || this.priceEl)) {
          this.form.addEventListener('change', this._scheduleUpdate);
          this.form.addEventListener('input', this._scheduleUpdate);
          this.form.addEventListener('click', this._scheduleUpdate);
        }

        // matchMedia listener (with Safari fallback for addListener).
        if (this.mql.addEventListener) {
          this.mql.addEventListener('change', this._applyMode);
        } else {
          this.mql.addListener(this._applyMode);
        }

        this._update();

        // Product-gallery images shift the trigger element down as they load; measuring
        // before then leaves it sitting too high, so the bar would wrongly stick on first
        // paint (and unstick again on the load reflow). Defer the initial setup until the
        // page is fully loaded so the first measurement runs against the settled layout.
        if (document.readyState === 'complete') {
          this._applyMode();
        } else {
          window.addEventListener('load', this._applyMode, { once: true });
        }
      }

      disconnectedCallback() {
        this._teardownScroll();
        if (this.priceObserver) this.priceObserver.disconnect();
        if (this.form) {
          this.form.removeEventListener('change', this._scheduleUpdate);
          this.form.removeEventListener('input', this._scheduleUpdate);
          this.form.removeEventListener('click', this._scheduleUpdate);
        }
        if (this.mql) {
          if (this.mql.removeEventListener) {
            this.mql.removeEventListener('change', this._applyMode);
          } else {
            this.mql.removeListener(this._applyMode);
          }
        }
        // Drop the deferred-setup listener if it never fired (disconnected before load).
        window.removeEventListener('load', this._applyMode);
        if (this._stickRaf) cancelAnimationFrame(this._stickRaf);
        if (this._updateRaf) cancelAnimationFrame(this._updateRaf);
        if (this._leaveTimer) clearTimeout(this._leaveTimer);
        this._lowerWidget();
      }

      // Enable scroll tracking only on mobile; on desktop force the button into flow.
      _applyMode() {
        if (this.mql.matches) {
          window.addEventListener('scroll', this._scheduleSticky, { passive: true });
          window.addEventListener('resize', this._scheduleSticky, { passive: true });
          this._evaluateSticky();
        } else {
          this._teardownScroll();
          this._setStuck(false);
        }
      }

      _teardownScroll() {
        window.removeEventListener('scroll', this._scheduleSticky);
        window.removeEventListener('resize', this._scheduleSticky);
      }

      // Throttle sticky evaluation to one per frame.
      _scheduleSticky() {
        if (this._stickRaf) return;
        this._stickRaf = requestAnimationFrame(() => {
          this._stickRaf = 0;
          this._evaluateSticky();
        });
      }

      // Primary: stick once the trigger element's BOTTOM edge reaches the stick line
      // (viewport bottom by default); release once it drops back past the hysteresis
      // line on scroll-up. Fallback: the inline bar is hidden on mobile, so the
      // sentinel is a zero-height marker at the button's original spot — stick when its
      // TOP rises to the stick line, release past the (lower) hysteresis line.
      _evaluateSticky() {
        if (this.triggerEl) {
          const bottom = this.triggerEl.getBoundingClientRect().bottom;
          const stickLine = window.innerHeight - NdogStickyAtc.TRIGGER_OFFSET;
          if (!this._stuck && bottom <= stickLine) {
            this._setStuck(true);
          } else if (this._stuck && bottom > stickLine + NdogStickyAtc.STICK_HYSTERESIS) {
            this._setStuck(false);
          }
          return;
        }
        const y = this.sentinel.getBoundingClientRect().top;
        const stickLine = window.innerHeight - NdogStickyAtc.STICK_OFFSET;
        if (!this._stuck && y <= stickLine) {
          this._setStuck(true);
        } else if (this._stuck && y > stickLine + NdogStickyAtc.STICK_HYSTERESIS) {
          this._setStuck(false);
        }
      }

      // Toggle the fixed state. The button's in-flow slot is allowed to collapse
      // (no reserved gap); the bar slides in/out via CSS animation. On leaving, the
      // bar stays fixed until the slide-out finishes, then returns to flow.
      _setStuck(stuck) {
        if (stuck === this._stuck) return;
        this._stuck = stuck;
        if (stuck) {
          if (this._leaveTimer) {
            clearTimeout(this._leaveTimer);
            this._leaveTimer = 0;
          }
          this.classList.remove('is-leaving');
          this.classList.add('is-stuck');
          if (this.info) this.info.setAttribute('aria-hidden', 'false');
          this._update();
          this._raiseWidget();
        } else {
          this.classList.remove('is-stuck');
          if (this.info) this.info.setAttribute('aria-hidden', 'true');
          if (this._reducedMotion) {
            this.classList.remove('is-leaving');
            this._lowerWidget();
            return;
          }
          // Keep the lift in place until the slide-out finishes, then release.
          this.classList.add('is-leaving');
          this._leaveTimer = setTimeout(() => {
            this._leaveTimer = 0;
            if (!this._stuck) {
              this.classList.remove('is-leaving');
              this._lowerWidget();
            }
          }, 280);
        }
      }

      // Resolve (and cache) the floating WhatsApp widget. It is injected async by a
      // third-party script, so it may not exist yet on the first scroll — re-query
      // until found, never cache a null.
      _getWidget() {
        if (!this._widget) {
          this._widget = document.querySelector('.chatarmin-widget-wrapper');
        }
        return this._widget;
      }

      // Lift the widget above the bar. The widget sets its `bottom` via an INLINE
      // style, which no external CSS can beat — so we write the inline style
      // directly (with !important) and remember the original to restore later.
      // Bar height is measured live (changes with optional info lines / safe-area).
      _raiseWidget() {
        const w = this._getWidget();
        if (!w) return;
        if (!this._widgetSaved) {
          this._widgetOrig = {
            bottom: w.style.getPropertyValue('bottom'),
            bottomPriority: w.style.getPropertyPriority('bottom'),
          };
          this._widgetSaved = true;
        }
        // A `bottom`-only transition (left in place) keeps the lift smooth in both
        // directions — it governs `bottom`, which only this component changes.
        w.style.setProperty('transition', 'bottom 0.28s cubic-bezier(0.4, 0, 0.2, 1)');
        w.style.setProperty('bottom', NdogStickyAtc.WIDGET_BOTTOM + 'px', 'important');
      }

      _lowerWidget() {
        const w = this._widget;
        if (!w || !this._widgetSaved) return;
        if (this._widgetOrig.bottom) {
          w.style.setProperty('bottom', this._widgetOrig.bottom, this._widgetOrig.bottomPriority);
        } else {
          w.style.removeProperty('bottom');
        }
        this._widgetSaved = false;
      }

      // Debounce info reads to one per frame.
      _scheduleUpdate() {
        if (this._updateRaf) cancelAnimationFrame(this._updateRaf);
        this._updateRaf = requestAnimationFrame(() => {
          this._updateRaf = 0;
          this._update();
        });
      }

      // Mirror the live config label and price into the sticky info element.
      _update() {
        if (this.configEl) {
          const config = this._readConfig();
          this.configEl.textContent = config;
          this.configEl.toggleAttribute('hidden', !config);
        }
        if (this.priceEl) {
          const { current, compare } = this._readPrice();
          this.priceEl.textContent = '';
          if (current) {
            this.priceEl.appendChild(document.createTextNode(current));
            // The struck "old" price gets its own <small> so it can be styled
            // (smaller, greyed, line-through) independently of the current price.
            if (compare) {
              this.priceEl.appendChild(document.createTextNode(' '));
              const old = document.createElement('small');
              old.className = 'ndog-sticky-atc__price-old';
              old.textContent = compare;
              this.priceEl.appendChild(old);
            }
          }
          this.priceEl.toggleAttribute('hidden', !current);
        }
        // Re-assert the lift (covers the widget being injected/re-rendered late).
        if (this._stuck) this._raiseWidget();
      }

      // Currently selected set size + variant, e.g. "60x · 400 g". The Sparset
      // amount (option2) leads so the chosen set is visible at a glance; products
      // without a second option fall back to the plain variant title.
      _readConfig() {
        const select = this.form && this.form.querySelector('select[name="id"]');
        if (select && select.selectedIndex >= 0) {
          const opt = select.options[select.selectedIndex];
          if (!opt) return '';
          const variantName = (opt.dataset.option1 || '').trim();
          // Legacy set values may carry a "(...)" suffix — too long for the bar, strip it.
          const setSize = (opt.dataset.option2 || '').split('(')[0].trim();
          if (variantName && setSize) return setSize + ' · ' + variantName;
          return opt.textContent.trim();
        }
        return '';
      }

      // Mirror the price the page shows. In the buy box #price .price__regular renders
      // <b> = the current amount and <s> = the struck "old" amount (empty when there is
      // no reduction). Returned split so each can be styled separately in the bar.
      _readPrice() {
        const regular = this.form && this.form.querySelector('#price .price__regular');
        if (regular) {
          const boldEl = regular.querySelector('b');
          const strikeEl = regular.querySelector('s');
          const current = boldEl ? boldEl.textContent.replace(/\s+/g, ' ').trim() : '';
          let compare = strikeEl ? strikeEl.textContent.replace(/\s+/g, ' ').trim() : '';
          // Bundle templates (legacy main-product.liquid) have no ndog-variant-picker, so
          // .price__regular s stays empty — there the theme JS writes the old amount into
          // .price__sale s instead. Use it as a fallback (never when it equals the current).
          if (!compare) {
            const saleStrike = this.form.querySelector('#price .price__sale s');
            const saleText = saleStrike ? saleStrike.textContent.replace(/\s+/g, ' ').trim() : '';
            if (saleText && saleText !== current) compare = saleText;
          }
          if (current) return { current, compare };
        }
        // Fallback: the selected option's data-price (no compare price available).
        const select = this.form && this.form.querySelector('select[name="id"]');
        if (select && select.selectedIndex >= 0) {
          const dp = select.options[select.selectedIndex].dataset.price;
          if (dp) return { current: dp.trim(), compare: '' };
        }
        return { current: '', compare: '' };
      }
    }
  );
}
